import Header from "@/atomic/atoms/Header";
import { InputMask } from "@react-input/mask";
import { PropsView } from "../Models/card";
import Button from "@/atomic/atoms/Button";
import { useState, useEffect, useRef } from "react";
import { validateCPF, validateCNPJ } from "@/utils/functions";
import Spinner from "@/atomic/atoms/Spinner";
import useI18n from "@/hooks/useI18n";
import { useThemeContext } from "@/contexts/ThemeContext";

export default function CheckoutCardView({
  register,
  handleSubmit,
  errors,
  goBack,
  isDisabled,
  error,
  isLoading,
  setValue,
  watch,
  clearErrors,
}: PropsView) {
  const { t } = useI18n();
  const { themeName } = useThemeContext();
  const title = themeName === 'zapay' ? 'Pedágio' : 'Pagamento';
  const [documentType, setDocumentType] = useState<'CPF' | 'CNPJ' | null>(null);
  const [documentValue, setDocumentValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const isForeign = watch('isForeign');

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    if (isForeign) {
      const cleanValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setDocumentValue(cleanValue);
      
      if (inputRef.current) {
        inputRef.current.value = cleanValue;
      }
      
      if (setValue) {
        setValue('document', cleanValue, { shouldValidate: true });
      }
    } else {
      const numericValue = value.replace(/\D/g, '');
      setDocumentValue(numericValue);
      
      let formattedValue = numericValue;
      
      if (numericValue.length === 11) {
        const cpfWithMask = numericValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        if (validateCPF(cpfWithMask)) {
          setDocumentType('CPF');
          formattedValue = cpfWithMask;
        } else {
          setDocumentType(null);
        }
      } else if (numericValue.length === 14) {
        const cnpjWithMask = numericValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        if (validateCNPJ(cnpjWithMask)) {
          setDocumentType('CNPJ');
          formattedValue = cnpjWithMask;
        } else {
          setDocumentType(null);
        }
      } else {
        setDocumentType(null);
      }
      
      if (inputRef.current) {
        inputRef.current.value = formattedValue;
      }
      
      if (setValue) {
        setValue('document', formattedValue, { shouldValidate: true });
      }
    }
  };

  const handleDocumentPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    
    if (isForeign) {
      const cleanValue = pastedText.replace(/[^a-zA-Z0-9]/g, '');
      setDocumentValue(cleanValue);
      if (setValue) {
        setValue('document', cleanValue, { shouldValidate: true });
      }
    } else {
      const cleanValue = pastedText.replace(/\D/g, '');
      
      if (cleanValue.length === 11) {
        const cpfWithMask = cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        if (validateCPF(cpfWithMask)) {
          setDocumentType('CPF');
          setDocumentValue(cleanValue);
          if (inputRef.current) {
            inputRef.current.value = cpfWithMask;
          }
          if (setValue) {
            setValue('document', cpfWithMask, { shouldValidate: true });
          }
        }
      } else if (cleanValue.length === 14) {
        const cnpjWithMask = cleanValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        if (validateCNPJ(cnpjWithMask)) {
          setDocumentType('CNPJ');
          setDocumentValue(cleanValue);
          if (inputRef.current) {
            inputRef.current.value = cnpjWithMask;
          }
          if (setValue) {
            setValue('document', cnpjWithMask, { shouldValidate: true });
          }
        }
      }
    }
  };

  const getFormattedValue = () => {
    if (isForeign) {
      return documentValue;
    }
    if (documentType === 'CPF' && documentValue.length === 11) {
      return documentValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (documentType === 'CNPJ' && documentValue.length === 14) {
      return documentValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return documentValue;
  };

  const handleIsForeignChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    
    if (!checked) {
      setDocumentValue('');
      setDocumentType(null);
      
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      
      if (setValue) {
        setValue('document', '', { shouldValidate: false });
        setValue('isForeign', false);
      }
      
      if (clearErrors) {
        clearErrors('document');
      }
    } else {
      if (setValue) {
        setValue('isForeign', true);
      }
    }
  };

  useEffect(() => {
    if (inputRef.current && documentValue.length > 0 && !isForeign) {
      const formattedValue = getFormattedValue();
      inputRef.current.value = formattedValue;
    }
  }, [documentType, documentValue, isForeign]);


  return (
    <div className="h-full">
      <Header
        title={title}
        hasLeftButton
        goBack={goBack}
      />
      <form className="p-2 w-full flex justify-center h-[calc(100%-72px)] text-black h-form bg-white" onSubmit={handleSubmit}>
        <article className="h-full flex flex-col w-[calc(480px)]">
          <div className="flex-1 overflow-y-auto pb-4">
            {error && <div className="flex w-full flex-col">
              <h2 className="text-2xl text-center font-bold">Houve um problema ao tentar realizar seu pagamento</h2>
              <h3 className="text-lg text-center">Clique no botão para tentar novamente</h3>
            </div>}
            {!error && <div>
              <h2 className="mt-6 text-black text-xl">Cartão de crédito</h2>
              <div className="flex flex-col justify-center items-center">
                <div className="w-full">
                  <InputMask
                    {...register("number")}
                    name="number"
                    required
                    type="text"
                    inputMode="numeric"
                    mask="____ ____ ____ ______"
                    replacement={{ _: /\d/ }}
                    placeholder="Número do cartão"
                    className="mt-3 text-base text-black w-full bg-transparent border p-2 rounded-lg h-14"
                  />
                  <div className="min-h-[1rem]">
                    {errors.number && (
                      <span className="text-red-500 text-xs break-words">{errors.number.message as string}</span>
                    )}
                  </div>
                </div>
                <div className="w-full">
                  <input
                    {...register("name")}
                    required
                    type="text"
                    name="name"
                    placeholder="Nome no cartão"
                    className="mt-3 text-base text-black w-full bg-transparent border p-2 rounded-lg h-14"
                  />
                  <div className="min-h-[1rem]">
                    {errors.name && (
                      <span className="text-red-500 text-xs break-words">{errors.name.message as string}</span>
                    )}
                  </div>
                </div>
                <div className="w-full">
                  <InputMask
                    {...register("expiration")}
                    name="expiration"
                    required
                    type="text"
                    inputMode="numeric"
                    mask="__/____"
                    replacement={{ _: /\d/ }}
                    placeholder="Vencimento MM/AAAA"
                    className="mt-3 text-base text-black w-full bg-transparent border p-2 rounded-lg h-14"
                  />
                  <div className="min-h-[1rem]">
                    {errors.expiration && (
                      <span className="text-red-500 text-xs break-words">{errors.expiration.message as string}</span>
                    )}
                  </div>
                </div>
                <div className="w-full">
                  
                  <input
                    {...register("document")}
                    ref={inputRef}
                    name="document"
                    required
                    type="text"
                    inputMode={isForeign ? "text" : "numeric"}
                    placeholder={isForeign ? t("pages:checkout:documentPlaceholderForeign") : "CPF/CNPJ"}
                    onChange={handleDocumentChange}
                    maxLength={isForeign ? 11 : 18}
                    onPaste={handleDocumentPaste}
                    className="mt-3 text-base text-black w-full bg-transparent border p-2 rounded-lg h-14"
                  />
                  <div className="min-h-[1rem]">
                    {errors.document && (
                      <span className="text-red-500 text-xs break-words">{errors.document.message as string}</span>
                    )}
                  </div>

                  <div className="flex items-center mt-3 mb-2">
                    <input
                      {...register("isForeign")}
                      type="checkbox"
                      id="isForeign"
                      className="w-5 h-5 accent-black mr-2"
                      onChange={handleIsForeignChange}
                    />
                    <label htmlFor="isForeign" className="text-sm text-black cursor-pointer">
                      {t("pages:checkout:isForeign")}
                    </label>
                  </div>
                </div>
              </div>
            </div>}
          </div>
          <div className="mt-auto pt-4 mb-4">
            <Button
              className="bg-primary text-white rounded-button"
              title={error ? "Tentar novamente" : "Finalizar"}
              type="submit"
              isDisabled={isDisabled}
            />
          </div>
        </article>
      </form>
      {isLoading && <Spinner />}
    </div>
  );
}